import React, { useEffect, useState } from 'react';
// import {
//   ColorPicker,
//   IColor,
//   IColorPickerStyles,
// } from 'office-ui-fabric-react/lib/index';
import {
  ColorPicker,
  getColorFromString,
  IColor,
  IColorPicker,
  IColorPickerStyles,
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { debounce } from 'underscore';


import ColorSelectorModal from './colorSelectorModal';

import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { TextField } from '@fluentui/react';
import { DEFAULT_UNDEFINED_COLOR } from '../constants/defaultStyleOptions';
import { ContainerName } from '../../Redux/reducers/AppReducer';
import { makeHueChangeVisible } from './colorPickerUtils';

const classNames = mergeStyleSets({
  column: { marginTop: '16px' },
  opacityColumn: {
    marginTop: '16px',
    width: '90px',
  },
  parent: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: '1rem',
    height: '100px',
  }
});

const colorPickerStyles: Partial<IColorPickerStyles> = {
  panel: { padding: 12 },
  root: {
    maxWidth: 300,
    minWidth: 300,
  },
  colorRectangle: { height: 268 },
};

const warningMessage = 'Hexadecimal colors must start with a "#" and can only contain 6 total alphanumeric values from [0-9] and [a-f]';

interface ColorSelectorProps {
  id: string;
  value: any;
  onChange: (styleElementName: string, value: any) => void;
  getStyleOptionValue: (key: any, containerName?: ContainerName) => any;
}

const removeHash = (colorCode: string) => {
  if (colorCode && colorCode.length > 0 && colorCode[0] === '#') {
    return colorCode.slice(1);
  }
  return colorCode;
}

const getOpacityFromColor = (colorCode: string) => {
  if (!colorCode) {
    return 100;
  }

  if (colorCode === 'Transparent') {
    return 0;
  }

  const value = removeHash(colorCode);
  if (value.length === 8) {
    const alphaHex = value.slice(6, 8);
    const alphaAsDecimal = parseInt(alphaHex, 16);
    if (!isNaN(alphaAsDecimal)) {
      return Math.round((alphaAsDecimal / 255) * 100);
    }
  }

  return 100;
}

const buildColorWithOpacity = (hexWithoutHash: string, opacityPercent: number) => {
  const baseHex = removeHash(hexWithoutHash).slice(0, 6);
  if (opacityPercent >= 100) {
    return `#${baseHex}`;
  }

  const alpha = Math.max(0, Math.min(100, opacityPercent));
  const alphaHex = Math.round((alpha / 100) * 255).toString(16).padStart(2, '0');
  return `#${baseHex}${alphaHex}`;
}

export const ColorSelector = (props: ColorSelectorProps) => {
  const getComputedValue = (value: string): any => {
    if(value === 'Transparent') {
      return '#ffffff00';
    }
    if(value && value.startsWith("$")) {
      
      let retFromStyleOptions = props.getStyleOptionValue(value.replace("$", ""), "styleOptions");
      let retFromUIOptions = props.getStyleOptionValue(value.replace("$", ""), "UI");
      let ret = retFromStyleOptions || retFromUIOptions;
      if((retFromStyleOptions && retFromStyleOptions.startsWith("$")) || (retFromUIOptions && retFromUIOptions.startsWith("$"))) {
        ret = getComputedValue(retFromStyleOptions || retFromUIOptions);
      }
      if(!ret) {
        return DEFAULT_UNDEFINED_COLOR;
      }
      return ret || DEFAULT_UNDEFINED_COLOR;
    } else {  
      return value || DEFAULT_UNDEFINED_COLOR;
    }
  }
  const { id, value, onChange } = props;
  const computedValue = getComputedValue(value);
  const pickerColor = getColorFromString(computedValue) || getColorFromString(DEFAULT_UNDEFINED_COLOR)!;
  const [textFieldValue, setTextFieldValue] = useState(removeHash(getComputedValue(value)).slice(0, 6));
  const [opacityFieldValue, setOpacityFieldValue] = useState(getOpacityFromColor(getComputedValue(value)).toString());
  const [isErrorMessageOpen, { setTrue: showErrorMessage, setFalse: hideErrorMessage }] = useBoolean(false);
  const [isOpacityErrorMessageOpen, { setTrue: showOpacityErrorMessage, setFalse: hideOpacityErrorMessage }] = useBoolean(false);

  const isValid = (newValue?: string): boolean => {
                    //  /^#([0-9A-F]{3}){1,2}$/i
    if (newValue && (/^#(([0-9A-F]{3}){1,2}|[0-9A-F]{8})$/i).test(`#${newValue}`)) {
      return true;
    }
    return false;
  }

  const isValidOpacity = (newValue?: string): boolean => {
    if (!newValue || !/^\d{1,3}$/.test(newValue)) {
      return false;
    }

    const parsedValue = parseInt(newValue, 10);
    return parsedValue >= 0 && parsedValue <= 100;
  }

  useEffect(() => {
    const computedValue = getComputedValue(value);
    setTextFieldValue(removeHash(computedValue).slice(0, 6));
    setOpacityFieldValue(getOpacityFromColor(computedValue).toString());
  }, [value]);

  const colorFormOnChange = (newValue?: string) => {
    if (isValid(newValue)) {
      setTextFieldValue(newValue as string);
      onChange(id, buildColorWithOpacity(newValue as string, parseInt(opacityFieldValue, 10) || 100));
      if (isErrorMessageOpen) {
        hideErrorMessage();
      }
    }
    else {
      if (newValue !== null && newValue !== undefined) {
        setTextFieldValue(newValue);
        if (!isErrorMessageOpen) {
          showErrorMessage();
        }
      }
    }
  }

  const opacityFormOnChange = (newValue?: string) => {
    if (isValidOpacity(newValue)) {
      const opacity = parseInt(newValue as string, 10);
      setOpacityFieldValue(newValue as string);
      if (isValid(textFieldValue)) {
        onChange(id, buildColorWithOpacity(textFieldValue, opacity));
      }
      if (isOpacityErrorMessageOpen) {
        hideOpacityErrorMessage();
      }
      return;
    }

    if (newValue !== null && newValue !== undefined) {
      setOpacityFieldValue(newValue);
      if (!isOpacityErrorMessageOpen) {
        showOpacityErrorMessage();
      }
    }
  }

  const updateColor = (colorObj: IColor) => {
    const pickerOpacity = typeof colorObj.a === 'number' ? colorObj.a : 100;
    onChange(id, buildColorWithOpacity(colorObj.hex, pickerOpacity));
    hideErrorMessage();
    hideOpacityErrorMessage();
    setTextFieldValue(colorObj.hex);
    setOpacityFieldValue(Math.round(pickerOpacity).toString());
  }

  const debounceUpdateColor = debounce(updateColor, 150)
 

  return (
    <div className={classNames.parent}>
      {
        <ColorSelectorModal colorValue={getComputedValue(value)} >
        <ColorPicker
          color={computedValue}
          onChange={(e: any, color: IColor) => {
            debounceUpdateColor(makeHueChangeVisible(pickerColor, color));
          }}
          showPreview
          styles={colorPickerStyles}
          strings={{
            alphaAriaLabel: 'Alpha slider: Use left and right arrow keys to change value, hold shift for a larger jump',
            transparencyAriaLabel: 'Transparency slider: Use left and right arrow keys to change value, hold shift for a larger jump',
            hueAriaLabel: 'Hue slider: Use left and right arrow keys to change value, hold shift for a larger jump',
          }}
          alphaType={'transparency'} />
      </ColorSelectorModal>
      }

      <div className={classNames.column}>
        <TextField value={textFieldValue} errorMessage={isErrorMessageOpen ? warningMessage : ''} prefix='#' onChange={(e: any, newValue?: string) => { colorFormOnChange(newValue) }} />
      </div>
      <div className={classNames.opacityColumn}>
        <TextField
          value={opacityFieldValue}
          errorMessage={isOpacityErrorMessageOpen ? 'Opacity must be an integer value between 0 and 100.' : ''}
          suffix={'%'}
          onChange={(e: any, newValue?: string) => { opacityFormOnChange(newValue) }}
        />
      </div>
    </div>
  );
};
